import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { Gate } from '../../../global/contexts/auth/auth';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { IconPlus } from '../../../common/icons';
import { storageLabel, isFrozen } from '../../../core/constants/transfers';
import { embryoSourceLabel, EMBRYO_STATUS_LABEL } from '../../../core/constants/embryo';
import LoadMore from '../../../common/components/LoadMore/LoadMore';
import { useEmbryosInfinite } from '../contexts/embryos';
import SectionTabs from '../../navigation/section-tabs';
import { OPU_TABS } from '../../navigation/tabs';

import '../../../common/styles/catalog.css';
import './styles/bank.css';

// Banco de embriones: inventario AGRUPADO POR GENÉTICA (Padre × Madre). La página
// principal muestra solo los DISPONIBLES (available, sin transferencia activa); un
// filtro permite ver también los transferidos. Filtros server-side (admin-embryos.md):
// q (código), storage, source, available. `storage=all` incluye frescos.
export default function EmbryoBank() {
    const [params, setParams] = useSearchParams();
    const urlSearch  = params.get('q') || '';
    const urlStorage = params.get('storage') || 'frozen'; // banco = preservados por defecto
    const urlSource  = params.get('source') || '';
    const urlAvail   = params.get('available') || 'true';  // principal = solo disponibles

    const [search, setSearch] = useState(urlSearch);
    const [debounced, setDebounced] = useState(urlSearch);
    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);
    useEffect(() => {
        const next = new URLSearchParams(params);
        if (debounced) next.set('q', debounced); else next.delete('q');
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next, { replace: true });
    };
    const clearFilters = () => { setSearch(''); setParams(new URLSearchParams(), { replace: true }); };

    const filters = useMemo(() => {
        const out = {};
        if (debounced) out.q = debounced;
        if (urlStorage !== 'all') out.storage = urlStorage;
        if (urlSource) out.source = urlSource;
        if (urlAvail !== 'all') out.available = true; // default y "true" → solo disponibles
        return out;
    }, [debounced, urlStorage, urlSource, urlAvail]);

    const { data, isLoading, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useEmbryosInfinite(filters);
    // Páginas acumuladas aplanadas: la agrupación por padre×madre corre sobre el acumulado.
    const items = useMemo(() => data?.pages?.flatMap(p => p.items ?? []) ?? [], [data]);
    const total = data?.pages?.[0]?.pagination?.total ?? items.length;
    const hasFilters = !!(search || urlStorage !== 'frozen' || urlSource || urlAvail !== 'true');
    const showAvail = urlAvail === 'all';

    // Agrupa por cruce genético (padre = sire, madre = donor). Clave por id (o nombre
    // si es externo sin id). Grupos ordenados por cantidad de embriones (desc).
    const groups = useMemo(() => {
        const map = new Map();
        for (const e of items) {
            const sireKey = e.sire?.id ?? e.sire?.name ?? 'none';
            const donorKey = e.donor?.id ?? e.donor?.name ?? 'none';
            const key = `${sireKey}|${donorKey}`;
            if (!map.has(key)) map.set(key, { sire: e.sire, donor: e.donor, embryos: [] });
            map.get(key).embryos.push(e);
        }
        return [...map.values()].sort((a, b) => b.embryos.length - a.embryos.length);
    }, [items]);

    return (
        <div className="cat-overview">
            <SectionTabs tabs={OPU_TABS} />
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Banco de embriones</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
                <Gate domain="embryos" action="write">
                    <Link to="/admin/embryos/new" className="cat-overview__add">
                        <IconPlus />
                        <span>Crear embrión</span>
                    </Link>
                </Gate>
            </header>

            <div className="cat-overview__toolbar">
                <div className="cat-overview__search">
                    <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por código…" aria-label="Buscar embriones por código" />
                </div>

                <select className="cat-overview__filter" value={urlStorage} onChange={(e) => setParam('storage', e.target.value)} aria-label="Filtrar por preservación">
                    <option value="frozen">Congelados</option>
                    <option value="fresh">Frescos</option>
                    <option value="all">Todos</option>
                </select>

                <select className="cat-overview__filter" value={urlSource} onChange={(e) => setParam('source', e.target.value)} aria-label="Filtrar por origen">
                    <option value="">Todo origen</option>
                    <option value="opu">OPU</option>
                    <option value="external">Externo</option>
                </select>

                <select className="cat-overview__filter" value={urlAvail} onChange={(e) => setParam('available', e.target.value)} aria-label="Filtrar por disponibilidad">
                    <option value="true">Solo disponibles</option>
                    <option value="all">Transferidos y disponibles</option>
                </select>

                {hasFilters && <button type="button" className="cat-overview__clear" onClick={clearFilters}>Limpiar</button>}
            </div>

            {error && <div className="cat-overview__error">{error.message || 'No se pudieron cargar los embriones'}</div>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando embriones…</div>
            ) : !items.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">{hasFilters ? 'Ningún embrión coincide con el filtro.' : 'No hay embriones disponibles en el banco.'}</p>
                </div>
            ) : (
                <div className={`emb-groups ${isFetching ? 'emb-groups--busy' : ''}`}>
                    {groups.map((g, gi) => (
                        <section key={`${g.sire?.id ?? g.sire?.name ?? 'ns'}-${g.donor?.id ?? g.donor?.name ?? 'nd'}-${gi}`} className="emb-group">
                            <header className="emb-group__head">
                                <div className="emb-cross">
                                    <div className="emb-parent emb-parent--sire">
                                        <span className="emb-parent__role">Padre</span>
                                        <span className="emb-parent__name" title={g.sire?.name || ''}>{g.sire?.name || 'Sin toro'}</span>
                                        {g.sire?.register && <span className="emb-parent__code" title={g.sire.register}>{g.sire.register}</span>}
                                    </div>
                                    <span className="emb-cross__x" aria-hidden="true">×</span>
                                    <div className="emb-parent emb-parent--donor">
                                        <span className="emb-parent__role">Madre</span>
                                        <span className="emb-parent__name" title={g.donor?.name || ''}>{g.donor?.name || 'Sin donadora'}</span>
                                        {g.donor?.number && <span className="emb-parent__code" title={g.donor.number}>{g.donor.number}</span>}
                                    </div>
                                </div>
                                <span className="emb-group__count">{g.embryos.length} embrión{g.embryos.length !== 1 ? 'es' : ''}</span>
                            </header>

                            <table className="cat-table">
                                <thead>
                                    <tr>
                                        <th>Código</th><th>Origen</th><th>Preserv.</th>
                                        {showAvail && <th>Disponibilidad</th>}
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {g.embryos.map((e) => (
                                        <tr key={e.id} className="cat-table__row">
                                            <td><Link to={`/admin/embryos/${e.id}`} className="cat-table__name">{e.code || `#${e.id}`}</Link></td>
                                            <td>{embryoSourceLabel(e.source)}</td>
                                            <td><span className={`emb-chip emb-chip--${isFrozen(e.storage) ? 'frozen' : 'fresh'}`}>{storageLabel(e.storage)}</span></td>
                                            {showAvail && <td>{e.transferred ? <span className="emb-chip emb-chip--used">Transferido</span> : <span className="emb-chip emb-chip--avail">Disponible</span>}</td>}
                                            <td><span className={`cat-badge cat-badge--${e.status}`}>{EMBRYO_STATUS_LABEL[e.status] || '—'}</span></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    ))}
                </div>
            )}

            {!isLoading && !error && items.length > 0 && (
                <LoadMore
                    shown={items.length}
                    total={total}
                    hasMore={!!hasNextPage}
                    loading={isFetchingNextPage}
                    onMore={() => fetchNextPage()}
                    noun="embriones"
                />
            )}
        </div>
    );
}
